import React, { useMemo } from "react";
import { Copy, Mail, MessageCircle } from "lucide-react";
import { usePanelTheme } from "../context/themeContext";

export default function InvoiceReminderToolbar({ invoice, client, company, lang = "de" }) {
  const { currentTheme, theme } = usePanelTheme();
  const labels = useMemo(
    () => ({
      title:
        lang === "sq"
          ? "Mjetet e rikujtesës"
          : lang === "de"
          ? "Erinnerungs-Tools"
          : "{labels.title}",
      subtitle:
        lang === "sq"
          ? "Dërgo rikujtesë për pagesën me copy, WhatsApp ose email."
          : lang === "de"
          ? "Sende eine Zahlungserinnerung per Kopie, WhatsApp oder E-Mail."
          : "Send a payment reminder by copy, WhatsApp or email.",
      copyReminder:
        lang === "sq"
          ? "Kopjo rikujtesën"
          : lang === "de"
          ? "Erinnerung kopieren"
          : "{labels.copyReminder}",
      whatsapp:
        lang === "sq" ? "WhatsApp" : lang === "de" ? "WhatsApp" : "WhatsApp",
      email:
        lang === "sq" ? "Email" : lang === "de" ? "E-Mail" : "Email",
    }),
    [lang]
  );


  const amount = Number(
    invoice?.grandTotal ?? invoice?.total ?? 0
  ).toFixed(2);

  const customerName =
    client?.name ||
    invoice?.clientName ||
    invoice?.companyName ||
    "Customer";

  const invoiceNumber = invoice?.invoiceNumber || invoice?.number || "—";
  const dueDate = invoice?.dueDate || "—";
  const companyName = company?.name || "BauHeld UG";
  const companyPhone = company?.phone || "";
  const clientEmail = client?.email || "";

  const reminderTemplates = {
  sq: `Përshëndetje ${customerName},

Kujtesë e shkurtër për pagesën e faturës ${invoiceNumber}.
Shuma: €${amount}
Afati i pagesës: ${dueDate}

Ju lutem kontrolloni pagesën sa më shpejt.

${companyName}${companyPhone ? `\n${companyPhone}` : ""}`,

  de: `Hallo ${customerName},

Dies ist eine kurze Erinnerung zur Zahlung der Rechnung ${invoiceNumber}.
Betrag: €${amount}
Fälligkeitsdatum: ${dueDate}

Bitte überprüfen Sie die Zahlung so bald wie möglich.

${companyName}${companyPhone ? `\n${companyPhone}` : ""}`,

  en: `Hello ${customerName},

This is a friendly reminder for invoice ${invoiceNumber}.
Amount: €${amount}
Due date: ${dueDate}

Please review the payment as soon as possible.

${companyName}${companyPhone ? `\n${companyPhone}` : ""}`,
};

const reminderText = reminderTemplates[lang] || reminderTemplates.de;

  const copyReminder = async () => {
    try {
      await navigator.clipboard.writeText(reminderText);
    } catch (error) {
      console.error("Failed to copy reminder:", error);
    }
  };

  const openWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(reminderText)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openEmail = () => {
    const subjectTexts = {
  sq: `Kujtesë për faturën ${invoiceNumber}`,
  de: `Zahlungserinnerung Rechnung ${invoiceNumber}`,
  en: `Invoice reminder ${invoiceNumber}`,
};

const subject = encodeURIComponent(subjectTexts[lang] || subjectTexts.de);
    const body = encodeURIComponent(reminderText);
    const url = `mailto:${clientEmail}?subject=${subject}&body=${body}`;
    window.location.href = url;
  };

  const shellClass =
    theme === "dark"
      ? "border-slate-700 bg-slate-900/70 text-slate-100"
      : "border-slate-200 bg-white text-slate-900";

  const buttonClass =
    theme === "dark"
      ? "border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-800"
      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50";

  return (
    <div className={`rounded-[28px] border p-5 shadow-sm ${shellClass}`}>
      <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-2xl ${
                theme === "dark"
                  ? "bg-slate-800 text-slate-200"
                  : "bg-slate-100 text-slate-700"
              }`}
            >
              <MessageCircle className="h-5 w-5" />
            </div>

            <div>
              <h3 className="text-xl font-bold tracking-tight">
                {labels.title}
              </h3>
              <p className={`mt-1 text-sm ${currentTheme.muted}`}>
                {labels.subtitle}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:min-w-[420px]">
          <button
            type="button"
            onClick={copyReminder}
            className={`inline-flex h-11 w-full items-center justify-center gap-2 rounded-2xl border px-4 text-sm font-semibold transition ${buttonClass}`}
          >
            <Copy className="h-4 w-4" />
            {labels.copyReminder}
          </button>

          <button
            type="button"
            onClick={openWhatsApp}
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 text-sm font-semibold text-white transition hover:bg-emerald-700"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </button>

          <button
            type="button"
            onClick={openEmail}
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            <Mail className="h-4 w-4" />
            Email
          </button>
        </div>
      </div>
    </div>
  );
}
